/**
 * preview.ts — pure helpers that render a ConditionSpec or ActionSpec
 * into a short English sentence. The DnD builder's live "preview" pane
 * stitches these into the marketing-style readout:
 *
 *   "When Outstanding conditions is greater than 1, send Email and WhatsApp
 *    to Title company."
 *
 * The helpers stay in @mana/shared so the API can render the same
 * preview when listing workflows (notification-log search, audit log,
 * etc.) — keeping the wording consistent across surfaces.
 */

import type {
  ConditionSpec,
  ConditionLiteral,
  ConditionOperator,
} from './conditions.js';
import { FIELD_CATALOG_BY_PATH, type LoanFieldDescriptor } from './loan-fields.js';
import type { ActionSpec } from './types.js';
import { describeRecipient, type NotificationRecipient } from './recipients.js';

// ---------------------------------------------------------------------------
// Operator phrasing
// ---------------------------------------------------------------------------

/**
 * Phrase an operator the way a human would read it. Numeric ops vary by
 * field type (a date "<" reads as "is before", a number "<" reads as
 * "is less than"), so the field descriptor is taken as a hint.
 */
function phraseOperator(
  op: Exclude<ConditionOperator, 'exists' | 'not_exists'>,
  field: LoanFieldDescriptor | undefined,
): string {
  const isDateLike = field?.type === 'date' || field?.type === 'datetime';
  switch (op) {
    case 'eq':
      return 'is';
    case 'neq':
      return 'is not';
    case 'gt':
      return isDateLike ? 'is after' : 'is greater than';
    case 'gte':
      return isDateLike ? 'is on or after' : 'is greater than or equal to';
    case 'lt':
      return isDateLike ? 'is before' : 'is less than';
    case 'lte':
      return isDateLike ? 'is on or before' : 'is less than or equal to';
  }
}

function phraseValue(value: ConditionLiteral, field: LoanFieldDescriptor | undefined): string {
  if (value === null) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  // String values for boolean-Y/N fields get a friendlier readout.
  if (field?.type === 'boolean_yn') {
    if (value === 'Y') return 'Yes';
    if (value === 'N') return 'No';
  }
  // Date / datetime literals are always quoted so the ISO delimiter
  // reads cleanly (and so "2026-06-15" is not mistaken for a bare word).
  if (field?.type === 'date' || field?.type === 'datetime') {
    return `"${value}"`;
  }
  // Strings: quote them so multi-word values read cleanly. Bare alnum
  // tokens (e.g. enum values like "Locked") render unquoted.
  return /^[A-Za-z0-9_]+$/.test(value) ? value : `"${value}"`;
}

function phraseField(path: string): { label: string; field: LoanFieldDescriptor | undefined } {
  const field = FIELD_CATALOG_BY_PATH[path];
  if (field) return { label: field.label, field };
  // Fallback: humanize the dot-path so we never render a raw "loan.foo".
  const last = path.split('.').pop() ?? path;
  const humanized = last.replace(/([A-Z])/g, ' $1').toLowerCase().trim();
  return { label: humanized.charAt(0).toUpperCase() + humanized.slice(1), field: undefined };
}

// ---------------------------------------------------------------------------
// describeConditionInEnglish
// ---------------------------------------------------------------------------

/**
 * Render a ConditionSpec as a single English phrase suitable for the
 * "When …" clause of the live preview. Pure and total.
 *
 * Examples:
 *   C.gt('loan.outstandingCondition', 1)
 *     -> "Outstanding conditions is greater than 1"
 *   C.eq('loan.cdSignedFlag', 'Y')
 *     -> "CD signed is Yes"
 *   C.exists('loan.closingDateTime')
 *     -> "Closing date/time is set"
 *   C.and(C.eq('loan.lockStatus', 'Locked'), C.gt('loan.outstandingCondition', 0))
 *     -> "Lock status is Locked AND Outstanding conditions is greater than 0"
 */
export function describeConditionInEnglish(condition: ConditionSpec): string {
  switch (condition.op) {
    case 'and':
      return condition.conditions.map(describeConditionInEnglish).join(' AND ');
    case 'or':
      return condition.conditions.map(describeConditionInEnglish).join(' OR ');
    case 'not':
      return `NOT (${describeConditionInEnglish(condition.condition)})`;
    case 'exists': {
      const { label } = phraseField(condition.path);
      return `${label} is set`;
    }
    case 'not_exists': {
      const { label } = phraseField(condition.path);
      return `${label} is not set`;
    }
    case 'eq':
    case 'neq':
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const { label, field } = phraseField(condition.path);
      return `${label} ${phraseOperator(condition.op, field)} ${phraseValue(condition.value, field)}`;
    }
  }
}

// ---------------------------------------------------------------------------
// describeActionInEnglish
// ---------------------------------------------------------------------------

/**
 * Render an ActionSpec as a short English phrase suitable for the
 * "… send X to Y" clause of the live preview.
 *
 * Examples:
 *   { kind: 'email', templateKey: 'cd-ready', to: 'title_company' }
 *     -> "send Email (cd-ready) to Title company"
 *   { kind: 'whatsapp', templateKey: 'lock-expiring', to: 'borrower' }
 *     -> "send WhatsApp (lock-expiring) to Borrower 1"
 *   { kind: 'internal_task', title: 'Call borrower', assignee: 'mlo' }
 *     -> 'create internal task "Call borrower" for MLO'
 */
export function describeActionInEnglish(action: ActionSpec): string {
  switch (action.kind) {
    case 'email':
      return `send Email (${action.templateKey}) to ${recipientLabel(action.to, action.customTo)}`;
    case 'whatsapp':
      return `send WhatsApp (${action.templateKey}) to ${recipientLabel(action.to, action.customTo)}`;
    case 'sms':
      return `send SMS (${action.templateKey}) to ${recipientLabel(action.to, action.customTo)}`;
    case 'internal_task': {
      const assignee = action.assignee === 'mlo' ? 'MLO' : action.assignee;
      return `create internal task "${action.title}" for ${assignee}`;
    }
  }
}

function recipientLabel(to: NotificationRecipient, customTo: string | undefined): string {
  if (to === 'custom' && customTo) return customTo;
  return describeRecipient(to).label;
}
